import React, {useEffect, useRef, useState} from 'react';
import ResizableDraggable from 'components/pictures/share/ResizableDraggable';
import PriceTag from 'components/pictures/PriceTag';
import DynamicScalingWrapper from 'components/pictures/share/DynamicScalingWrapper';
import {getImageUrl} from 'util/configuration';
import {CoinyLogoMonocolorShadowed} from "../../../util/svg/logo/ShapesOfCoinSvg";
import TrophyScalingWrapper from "./TrophyScalingWrapper";
import 'styles/pictures/display/PictureItem.css';
import MedalSvg from "../../../util/svg/share/MedalSvg";
import FireSvg from "../../../util/svg/FireSvg";

const ShareCanvas = ({
                         items,
                         imageData,
                         selectedItem,
                         setSelectedItem,
                         imageRanks,
                         showLetters,
                         resetKey,
                         transparencies,
                         onImageLoad,
                     }) => {
    const [positions, setPositions] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);

    const imageRef = useRef(null);

    const handlePositions = (width, height) => {

        const localPadding = width / 30;

        const fireX = localPadding * 1.7;
        const fireY = localPadding * 1.2;
        const fireSize = width / 8;

        const trophySize = width / 5;
        const trophyX = (width - trophySize) / 2;

        const pricetagSize = width / 4;
        const pricetagX = width - pricetagSize - localPadding;

        const logoPadding = localPadding * 1.5;
        const logoSize = Math.min(width / 3, height / 3);
        const logoY = height - logoSize - logoPadding;

        const newPositions = {
            'fire': {x: fireX, y: fireY, width: fireSize, height: fireSize},
            'trophy': {x: trophyX, y: localPadding, width: trophySize, height: trophySize},
            'pricetag': {x: pricetagX, y: localPadding, width: pricetagSize, height: pricetagSize},
            'logo': {x: logoPadding, y: logoY, width: logoSize, height: logoSize},
        };
        setPositions(newPositions);
    };

    useEffect(() => {
        if (!imageRef.current) {
            return;
        }

        const ro = new ResizeObserver(([entry]) => {
            const {width, height} = entry.contentRect;
            if (width > 0 && height > 0) {
                handlePositions(width, height);
            }
        });

        ro.observe(imageRef.current);
        return () => ro.disconnect();
    }, []);

    return (
        <div
            id="image-container"
            style={{
                position: 'relative',
                overflow: 'hidden',
                backgroundImage: `url(${getImageUrl(imageData.id, 'scrollable')})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
        >
            {positions && imageLoaded && items.map((item) => (
                <ResizableDraggable
                    key={item + '-' + resetKey}
                    enabled={selectedItem === item}
                    flagName={item}
                    defaultPosition={positions[item]}
                    dragStart={(flagName) => setSelectedItem(flagName)}
                    minWidth={'50px'}
                >
                    {item === 'logo' && (
                        <div style={{width: '100%', height: '100%', opacity: transparencies['logo']}}>
                            <CoinyLogoMonocolorShadowed size={'100%'} showLetters={showLetters} color={'#18471c'}
                                                        detailColor={'#8da690'}/>
                        </div>
                    )}

                    {item === 'pricetag' && (
                        <div style={{width: '100%', height: '100%', opacity: transparencies['pricetag']}}>
                            <DynamicScalingWrapper cash={imageData.getAmount()}>
                                <PriceTag forShare={true} amount={imageData.getAmount()}/>
                            </DynamicScalingWrapper>
                        </div>
                    )}

                    {item === 'fire' && (
                        <div style={{width: '100%', height: '100%', opacity: transparencies['fire']}}>
                            <TrophyScalingWrapper>
                                <div className="badge">
                                    <FireSvg width='100%'/>
                                    <span className="fire-text share">
                                        {imageRanks.trending + '.'}
                                    </span>
                                </div>
                            </TrophyScalingWrapper>
                        </div>
                    )}

                    {item === 'trophy' && imageRanks.top <= 3 && (
                        <div style={{width: '100%', height: '100%', opacity: transparencies['trophy']}}>
                            <TrophyScalingWrapper>
                                <div className="badge">
                                    <img src="/images/position/trophy.png" alt="Order badge"/>
                                    <span className="trophy-number">{imageRanks.top}</span>
                                </div>
                            </TrophyScalingWrapper>
                        </div>
                    )}

                    {item === 'trophy' && imageRanks.top > 3 && (
                        <div style={{width: '100%', height: '100%', opacity: transparencies['trophy']}}>
                            <TrophyScalingWrapper>
                                <div className="badge">
                                    <MedalSvg size={'100%'}/>
                                    <span className="medal-text">
                                        {imageRanks.top}
                                    </span>
                                </div>
                            </TrophyScalingWrapper>
                        </div>
                    )}
                </ResizableDraggable>
            ))}

            <img
                ref={imageRef}
                src={getImageUrl(imageData.id, 'scrollable')}
                crossOrigin="anonymous"
                className="stretched-image"
                alt="Share preview"
                onLoad={(e) => {
                    handlePositions(e.target.width, e.target.height);
                    setImageLoaded(true);
                    onImageLoad();
                }}
                onDragStart={(e) => e.preventDefault()}
            />
        </div>
    );
};

export default ShareCanvas;
